import { FC, useMemo } from "react";
import { Strategy } from "../../../store/slices/strategySlice";
import autoBot from "../assets/autobotBig.png";
import { Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import CardHeader from "../../../_components/CardHeader";
import CustomPaper from "../../../_components/CustomPaper";
import Badge from "../../../_components/Badge";
import PriceRange from "../../../_components/PriceRange";
import DottedTextField from "../../../_components/DottedTextField";
import PairInfo from "../../../_components/PairInfo";
import Scale from "../../../_components/Scale";
import { formatDate } from "../../../_utils/formatDate";
import formatNumber from "../../../_utils/formatNumber";
import { precision } from "../../../_utils/precision";
import { useAppNavigate } from "../../../_hooks/use-app-navigate";
import Text from "../../../_components/Text";
import { useTranslation } from "react-i18next";

interface AutoStrategyCardProps {
  strategy: Strategy;
}

const AutoStrategyCard: FC<AutoStrategyCardProps> = ({ strategy }) => {
  const theme = useTheme();
  const appNavigate = useAppNavigate();
  const { t } = useTranslation("botsList");
  const tickers = useSelector((state: RootState) => state.shared.tickers);

  const strategyType = useParams<{ type: string }>()?.type;

  const changeLocation = (strategyId: string) => {
    if (strategyType) {
      appNavigate.strategyDetail(strategyType === "active" ? "active" : "archived", strategyId);
    } else {
      appNavigate.strategyDetail("active", strategyId);
    }
  };

  const tickerPriceStep = useMemo(() => {
    let foundPriceStep = tickers.find(
      (ticker) => ticker.base === strategy.base && ticker.quote === strategy.quote
    )?.priceStep;
    if (foundPriceStep) {
      return precision(foundPriceStep);
    } else {
      return 4;
    }
  }, [strategy.base, strategy.quote, tickers]);

  return (
    <>
      <Stack direction="column" justifyContent={"center"} spacing={0}>
        <CardHeader value={strategy.displayedId} status={strategy.status} />
        <CustomPaper
          sx={{
            position: "relative",
            backgroundImage: `url(${autoBot})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "40%",
            backgroundPositionX: "center",
            backgroundPositionY: "bottom",
            width: "100%",
            borderTopLeftRadius: "0 !important",
            borderTopRightRadius: "0 !important",
          }}
          onClick={() => changeLocation(strategy.id)}
        >
          <Stack direction={"column"} justifyContent={"center"} spacing={1}>
            <Stack direction={"row"} justifyContent={"space-between"} spacing={1}>
              <Stack direction="row" alignItems="center">
                <PairInfo base={strategy.base} quote={strategy.quote} />

                <Text variant="regular-semi"> {`${strategy.base} / ${strategy.quote}`}</Text>
              </Stack>

              <Badge
                height={20}
                icon={<Scale size={16} />}
                value={`${strategy.leverage}x`}
                variant="outlined"
              />
            </Stack>

            <Stack direction={"column"} justifyContent={"center"} spacing={"3px"}>
              <Stack direction={"row"} alignItems={"flex-end"} justifyContent={"space-between"}>
                <Stack direction={"column"} spacing={"1px"}>
                  {strategy.status !== "RUNNING" && strategy.closeTime && (
                    <Text variant="s" color={theme.myTheme.colors.foreground20}>
                      {`${t("closing")} ${formatDate(strategy.closeTime)}`}
                    </Text>
                  )}

                  {strategy.startTime && (
                    <Text variant="s" color={theme.myTheme.colors.foreground20}>
                      {`${t("opening")} ${formatDate(strategy.startTime)}`}
                    </Text>
                  )}
                </Stack>
              </Stack>
            </Stack>

            <PriceRange
              minPrice={strategy.minPrice}
              maxPrice={strategy.maxPrice}
              gridDirection={strategy.gridDirection}
              quote={strategy.quote}
              orderStep={tickerPriceStep}
            />
            <Stack direction={"column"} justifyContent={"center"} spacing={1}>
              <DottedTextField
                placeholder={t("volume")}
                value={`${formatNumber(strategy.investmentQuote, 2)} ${strategy.quote}`}
              />
              <DottedTextField
                placeholder={t("margin")}
                value={`${formatNumber(strategy.requiredMargin, 2)} ${strategy.quote}`}
              />
              <DottedTextField
                // TODO Перевод
                placeholder={`Bots PnL`}
                value={`$${formatNumber(strategy.realizedPnl + strategy.unrealizedPnl, 2)}`}
              />
              <DottedTextField
                // TODO Перевод
                placeholder={`Position PnL`}
                value={`$${formatNumber(strategy.strategyPositionRealizedPnl + strategy.unrealizedStrategyPositionPnl, 2)}`}
              />
              <DottedTextField
                placeholder={t("totalPnl")}
                value={`$${formatNumber(strategy.totalRealizedPnl + strategy.totalUnrealizedPnl, 2)}`}
              />
            </Stack>
          </Stack>
        </CustomPaper>
      </Stack>
    </>
  );
};

export default AutoStrategyCard;
